import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { ICEPACK_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	createIcepackRequest,
	deleteIcepackRequest,
	getIcepackInfoRequest,
	updateIcepackRequest,
} from '../../api/requests';
import { getPartnersRequest } from '../../api/partners-requests';
import {
	GET_ICEPACK_INFO,
	setIcepackInfoAction,
	setIsIcepackInfoLoadingAction,
	CREATE_ICEPACK,
	setIsIcepackCreatingAction,
	UPDATE_ICEPACK,
	setIsIcepackUpdatingAction,
	DELETE_ICEPACK,
	setIsIcepackDeletingAction,
	GET_PARTNERS,
	setPartnersAction,
	setIsPartnersLoadingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { getIcepacksAction } from '../icepacks/actions';
import { transformToValidationErrors } from './utils';

export function* createIcepackSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createIcepackRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getIcepacksAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackCreatingAction(false));
	}
}

export function* getIcepackInfoSaga({ payload: id }) {
	try {
		yield put(setIsIcepackInfoLoadingAction(true));

		const { data, message, toast } = yield call(getIcepackInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setIcepackInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackInfoLoadingAction(false));
	}
}

export function* updateIcepackSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateIcepackRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setIcepackInfoAction(data));

			redirect();

			yield put(getIcepacksAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackUpdatingAction(false));
	}
}

export function* deleteIcepackSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackDeletingAction(true));

		const { data, message, toast } = yield call(deleteIcepackRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getIcepacksAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackDeletingAction(false));
	}
}

export function* getPartnersSaga() {
	try {
		yield put(setIsPartnersLoadingAction(true));

		const { data, message, toast } = yield call(getPartnersRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setPartnersAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPartnersLoadingAction(false));
	}
}

export default function* icepackModalSaga() {
	yield takeEvery(GET_ICEPACK_INFO, getIcepackInfoSaga);
	yield takeEvery(CREATE_ICEPACK, createIcepackSaga);
	yield takeEvery(UPDATE_ICEPACK, updateIcepackSaga);
	yield takeEvery(DELETE_ICEPACK, deleteIcepackSaga);
	yield takeEvery(GET_PARTNERS, getPartnersSaga);
}
